import { useEffect, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store'
import { fetchCurrentUser } from '../slices/authSlice'
import { login } from '../components/UserMenu'
import { useGetTeamInviteInfoQuery, useJoinTeamByInviteMutation } from '../services/projectDataApi'
import Spinner from '../components/Spinner'
import './InvitePage.css'

// The landing an invited person reaches from a team invite link — the email button
// (lib/team-invite-email.ts) and the copyable link in Settings › Team both point here.
//
// It renders without the app chrome because the recipient is usually logged out and may not have an
// account at all; being greeted by authed navigation would be the wrong first impression. Signed-out
// visitors get the team's name and the two ways in; a signed-in one joins immediately and lands in
// the app, since there is nothing left to ask them.
//
// Holding the token IS the authorization, so joining needs no invite row to exist first — which is
// what makes a forwarded link work for someone we never emailed.

// RTK Query surfaces a rejected mutation as { status, data }, and the API marks a plan refusal with a
// code rather than only prose so this doesn't have to match on the message.
function isUpgradeRequired(err: unknown): boolean {
  const data = (err as { data?: { code?: string } } | null)?.data
  return data?.code === 'upgrade_required'
}

export default function JoinTeamPage() {
  const { token } = useParams<{ token: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { user, checked, loading } = useSelector((state: RootState) => state.auth)
  const [joinTeam] = useJoinTeamByInviteMutation()
  const [joined, setJoined] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const attempted = useRef(false)

  useEffect(() => {
    if (!checked) dispatch(fetchCurrentUser())
  }, [dispatch, checked])

  const { data: info, isLoading: infoLoading, isError: infoError } = useGetTeamInviteInfoQuery(token ?? '', {
    skip: !token,
  })

  // Signed in and the link resolves: join once, then hand them the app. Joining does not re-point
  // anything else — a new project still defaults to their own team, and the "Select team" picker at
  // creation is where they can put one in the team they just accepted (see useProjectTeam).
  useEffect(() => {
    if (!token || !user || !info || attempted.current) return
    attempted.current = true
    void joinTeam(token)
      .unwrap()
      .then(() => setJoined(true))
      .catch((err: unknown) => {
        // A team whose plan no longer includes collaboration refuses the join (see
        // lib/team-plan.ts). That is the owner's to fix, and saying so is the difference between
        // "the link is broken" and "ask them to upgrade".
        setError(
          isUpgradeRequired(err)
            ? 'This team’s plan doesn’t include teammates right now. Ask whoever invited you to upgrade, then use the link again.'
            : 'We couldn’t add you to this team. The link may have been replaced.',
        )
      })
  }, [info, joinTeam, token, user])

  if (!token) return <Navigate to="/" replace />
  if (joined) return <Navigate to="/" replace />

  const teamName = info?.name?.trim() || null
  const heading = teamName ? `You’ve been invited to ${teamName}` : 'You’ve been invited to a Replay QA team'

  // Wait for the auth check and the invite lookup before deciding what to show, so a signed-in
  // visitor never sees the sign-in CTAs flash before their automatic join, and the heading doesn't
  // rewrite itself from a generic placeholder to the team's name.
  if (!checked || loading || infoLoading || (user && !error && !infoError)) {
    return (
      <div className="invite-page">
        <div className="invite-page__loading">
          <Spinner size={28} />
        </div>
      </div>
    )
  }

  return (
    <div className="invite-page">
      <div className="invite-page__card" data-testid="join-team-page">
        <div className="invite-page__brand">
          <img src="/images/logo.svg" alt="" width="26" height="30" className="invite-page__logo" />
          <span className="invite-page__wordmark">Replay QA</span>
        </div>

        <h1 className="invite-page__title">{heading}</h1>
        {infoError || error ? (
          <p className="invite-page__note" data-testid="join-team-error">
            {error ?? 'This invite link is no longer valid. Ask whoever invited you for a new one.'}
          </p>
        ) : (
          <p className="invite-page__note">
            Replay QA automatically explores your app, files the bugs it finds, and keeps testing as the
            app changes. Joining {teamName ? `${teamName}` : 'the team'} gives you its projects and QA
            results
            {info && info.member_count > 0
              ? ` alongside ${info.member_count} ${info.member_count === 1 ? 'teammate' : 'teammates'}`
              : ''}
            , and the runs you start are covered by the team’s credits.
          </p>
        )}

        {!infoError && (
          <div className="invite-page__actions">
            <div className="invite-page__action">
              <span className="invite-page__action-label">Already have a Replay QA account?</span>
              <button
                type="button"
                className="invite-page__btn invite-page__btn--primary"
                data-testid="join-team-sign-in"
                data-analytics-event="join-team-sign-in"
                onClick={() => login(dispatch, `/join/${token}`)}
              >
                Sign In
              </button>
            </div>

            <div className="invite-page__action">
              <span className="invite-page__action-label">New to Replay QA?</span>
              <button
                type="button"
                className="invite-page__btn invite-page__btn--secondary"
                data-testid="join-team-create-account"
                data-analytics-event="join-team-create-account"
                onClick={() => login(dispatch, `/join/${token}`, 'signup')}
              >
                Create an account
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
